<template>
    <modal
        v-if="show"
        class="complianceModal"
        :title="'Enable CNAM Caller ID'"
        :form="form"
        :submitText="submitTextModal"
        :cancelText="'Cancel'"
        :backText="'Previous'"
        :submitClasses="{
            'is-primary': true,
            'is-busy': form.busy,
        }"
        :actionLeft="actionLeft"
        :submitDisabled="submitDisabled"
        :hideBack="!showBackButton"
        :hideCancel="!showCancelButton"
        @back="handlePreviousStep"
        @closed="handleClosed"
        @submit="handleNextStep"
    >
        <div v-if="loading" class="complianceModal__content">
            <basic-loader center aria-label="CNAMCallerID" />
        </div>
        <template v-else>
            <configuration-stepper :type="'simple'" :currentStep="currentStep" :steps="steps" />

            <div v-if="currentStep === 1" class="complianceModal__content complianceModal__preview">
                <div class="complianceModal__previewTitle">CNAM | Display your business name</div>

                <div class="complianceModal__previewDescription">
                    Brand your outgoing calls with your company name using <br />
                    our verified Caller ID (CNAM) service.
                </div>

                <div class="complianceModal__previewImage">
                    <img :src="`${$window.Config.cdn_url}/images/cnam.png`" alt="" />
                </div>
            </div>

            <div v-if="currentStep === 2" class="complianceModal__content complianceModal--cnam">
                <div class="complianceModal__title">CNAM Display Name</div>

                <div class="complianceModal__column">
                    <form-input
                        v-model="form.name"
                        :label="'Enter the brand name you want to display as your Caller ID'"
                        :error="form.errors.get('name')"
                        :hideLabelColon="true"
                        :tooltip="'This is the name that will appear on the recipients caller ID. You can display up to 15 characters and will be vetted for appropriateness and relevance to your business.'"
                    />
                </div>

                <div class="complianceModal__title">Register phone numbers for CNAM</div>

                <div class="complianceModal__column">
                    <form-dropdown
                        id="dropdown-phone_numbers"
                        ref="select"
                        :label="'Select your Salesmsg number that you want to add a Caller ID to'"
                        :error="form.errors.get('phone_numbers')"
                        :hideLabelColon="true"
                        :settings="dropdownSettings"
                        dropdown-class="selectize-contacts"
                        @input="updateSelected"
                    />
                </div>
            </div>

            <div v-if="currentStep === 3" class="complianceModal__content complianceModal--cnam">
                <div class="complianceModal__title">Terms of Service and Pravicy Policy</div>

                <div class="complianceModal__column">
                    <form-checkbox
                        v-model="form.terms_accepted"
                        :error="form.errors.get('terms_accepted')"
                        :position="'top'"
                    >
                        I certify that my business ({{ profileBusiness.businessName }}) is the
                        originator of the phone calls and certify that the display name represents
                        my business.
                    </form-checkbox>
                </div>
            </div>
        </template>
    </modal>
</template>

<script>
import { mapState } from 'pinia';
import stores from 'store/modules';
import Form from 'core/Form';

export default {
    data() {
        return {
            loading: true,
            form: new Form({
                name: '',
                phone_numbers: [],
                terms_accepted: false,
            }),
            currentStep: 1,
            steps: 3,
            showBackButton: false,
            showCancelButton: true,
            actionLeft: true,
        };
    },

    computed: {
        show() {
            return this.showCNAMCallerIDModal;
        },

        submitTextModal() {
            return this.currentStep === this.steps ? 'Confirm' : 'Next';
        },

        numbers() {
            return this.CNAMCallerFreeNumbers;
        },

        dropdownSettings() {
            return {
                valueField: 'id',
                searchField: ['label'],
                options: this.numbers,
                highlight: true,
                render: {
                    item: this.dropdownItem,
                    option: this.dropdownOption,
                },
                maxItems: null,
                dropdownParent: 'body',
                plugins: ['remove_button'],
            };
        },

        submitDisabled() {
            if (this.currentStep === 1) {
                return false;
            }

            if (this.currentStep === 2) {
                return false;
            }

            if (this.currentStep === 3) {
                return Boolean(!this.form.terms_accepted);
            }

            return false;
        },

        ...mapState(stores.ComplianceProfile, [
            'profileBusiness',
            'CNAMCallerFreeNumbers',
            'complianceBillingInfo',
        ]),
        ...mapState(stores.CallingStore, ['showCNAMCallerIDModal']),
    },

    watch: {
        currentStep(value) {
            if (value === 2) {
                this.initForm();
            }
        },

        show(value) {
            if (value) {
                this.init();
            }
        },
    },

    methods: {
        async init() {
            await this.reset();
            await this.initNumbers();
            await this.calculateButtons();
        },

        async initForm() {
            await this.$delay(200);

            if (this.$refs.select.dropdown) {
                this.$refs.select.dropdown.addItems(this.form.phone_numbers);
            }
        },

        async initNumbers() {
            try {
                this.loading = true;

                await this.$store.dispatch('fetchCNAMCallerFreeNumbers');
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'initNumbers');
            } finally {
                await this.$delay(200);

                this.loading = false;
            }
        },

        reset() {
            this.form.reset();
            this.currentStep = 1;
            this.showBackButton = true;
            this.showCancelButton = false;
            this.actionLeft = true;
        },

        setStep(step) {
            this.currentStep = step;
            this.calculateButtons();
        },

        calculateButtons() {
            if (this.currentStep > 1) {
                this.showBackButton = true;
                this.showCancelButton = false;
            } else {
                this.showBackButton = false;
                this.showCancelButton = true;
            }
        },

        async handleNextStep() {
            if (this.currentStep === 1) {
                this.setStep(2);

                return;
            }

            if (this.currentStep === 2) {
                try {
                    await this.$store.dispatch('validateCNAMCaller', this.form);

                    this.form.successful = false;
                    this.form.errors.clear();
                    this.setStep(3);
                } catch (e) {
                    this.$log.error(e, this.$options.__file, 'handleNextStep');
                }

                return;
            }

            if (this.currentStep === 3) {
                try {
                    await this.$store.dispatch('termsCNAMCaller', this.form);
                    await this.$store.dispatch('createCNAMCaller', this.form);
                } catch (e) {
                    this.$log.error(e, this.$options.__file, 'handleNextStep');
                }
            }
        },

        handlePreviousStep() {
            if (this.currentStep === 2) {
                this.setStep(1);

                return;
            }

            if (this.currentStep === 3) {
                this.setStep(2);
            }
        },

        handleClosed() {
            this.$store.dispatch('handleShowCNAMCallerIDModal', false);
            this.$emit('closed');
        },

        dropdownOption(item, escape) {
            return `<div class="selectize-contact">
                <div class="selectize-contact-info">
                    <strong>${item.label}</strong>
                </div>
            </div>`;
        },

        dropdownItem(item, escape) {
            return `<div class="selectize-contact tag is-small is-blue">
                        ${item.label}
                    </div>`;
        },

        updateSelected(ids) {
            ids = ids.map((id) => +id);

            this.form.phone_numbers = ids.map((id) => +id);
        },
    },
};
</script>
